import React, { FC, useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import BackdropProgressBar from "../../../../components/ui/BackdropProgressBar";
import useApi from "../../../../hooks/useApi";
import useContainerWidth from "../../../../hooks/useContainerWidth";
import { useToaster } from "../../../../hooks/useToaster";
import { SEVERITIES } from "../../../../data/constants";
import { APPROVAL_ROLES, approvePifById, rejectPifById, resendPifApprovalLink } from "../../../../services/pifService";
import { PifApprovalOutcome, PifApprovalRecord } from "../types";
import PifApprovalChainRail from "./PifApprovalChainRail";
import PifApprovalRecordPanel from "./PifApprovalRecordPanel";
import PifApprovalDecisionPanel from "./PifApprovalDecisionPanel";
import { formatExpiresIn } from "./approvalHelpers";
import "./PifApprovalReview.scss";

const WIDE_COL = 940;

// The approve/reject endpoints return only a result code (no server timestamp), so the done screen
// shows a local confirmation stamp — the authoritative signed time lives on the signature row.
const actionStamp = (): string =>
  new Date().toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

interface PifApprovalReviewProps {
  record: PifApprovalRecord;
  signerName: string;
  onBack?: () => void;
  onOutcome: (outcome: PifApprovalOutcome, pifId: string, signedAt: string) => void;
}

const PifApprovalReview: FC<PifApprovalReviewProps> = ({ record, signerName, onBack, onOutcome }) => {
  const roleMeta = APPROVAL_ROLES[record.currentRole];
  // `loading` covers the approve/reject POSTs below. Without it the screen sat inert while the
  // signature was being written — no spinner, and the decision buttons still live for a second
  // click. The backdrop is modal, so it doubles as the double-submit guard.
  const { loading, executeApi } = useApi();
  const showToaster = useToaster();
  const [rootRef, width] = useContainerWidth<HTMLDivElement>();

  // Only the viewer who performed the step immediately before the pending signer can nudge them —
  // the previous approver, or the creator while the chain is still at its first stage (Bug 82334:
  // the submitter used to keep this for the whole life of the form, and every past approver got it
  // at once). Every other viewer just gets "Back to list", and so does the current signer — they
  // act via the decision panel below, not resend.
  const canResend = !record.viewerIsCurrentSigner && record.viewerIsPreviousActor;

  const chainDisplay = useMemo(
    () =>
      record.viewerIsCurrentSigner
        ? record.chain.map((c) => (c.state === "current" ? { ...c, name: signerName } : c))
        : record.chain,
    [record.chain, record.viewerIsCurrentSigner, signerName],
  );

  const wide = width >= WIDE_COL;
  const hasRail = chainDisplay.length > 0 || record.audit.length > 0;
  // Two columns only when the rail actually has a card — an empty left column would stay reserved
  // under --wide even if PifApprovalChainRail returned null.
  const gridClass = `pif-approval-review__grid${wide && hasRail ? " pif-approval-review__grid--wide" : ""}`;

  const handleSign = async () => {
    const result = await executeApi(approvePifById, record.postIfId);
    if (result) onOutcome("signed", record.pifId, actionStamp());
  };

  const handleSendBack = async (note: string) => {
    const result = await executeApi(rejectPifById, { postIfId: record.postIfId, reason: note, final: false });
    if (result) onOutcome("changes", record.pifId, actionStamp());
  };

  const handleDecline = async (note: string) => {
    const result = await executeApi(rejectPifById, { postIfId: record.postIfId, reason: note, final: true });
    if (result) onOutcome("declined", record.pifId, actionStamp());
  };

  const handleResend = async () => {
    const result = await executeApi(resendPifApprovalLink, record.postIfId);
    if (result) showToaster("A new signature link has been sent to the current approver.", SEVERITIES.SUCCESS);
  };

  return (
    <Box className="pif-approval-review" ref={rootRef}>
      <BackdropProgressBar loading={loading} />

      {/* No title line here: the page band directly above already prints "Post Interview Form" and
          the record ref as the terminal breadcrumb, so a "{pifId} · Post Interview Form" heading
          plus a "{pifId} · submitted" subtitle repeated the ref three times and the page title
          twice within one screen height (Epic 79820). The strip now carries only what the band
          can't — the record's status and the viewer's standing. */}
      <Box className="pif-approval-review__topbar">
        <Typography className="pif-approval-review__topbar-chip pif-approval-review__topbar-chip--status">
          {record.statusLabel}
        </Typography>
        <Box className="pif-approval-review__topbar-chips">
          <Typography className="pif-approval-review__topbar-chip">
            {record.viewerIsCurrentSigner ? "Your signature is next" : "View only"}
          </Typography>
          {record.expiresAt && (
            <Typography className="pif-approval-review__topbar-chip pif-approval-review__topbar-chip--muted">
              {formatExpiresIn(record.expiresAt)}
            </Typography>
          )}
          {canResend ? (
            <Button
              variant="outlined"
              size="small"
              onClick={handleResend}
              className="pif-approval-review__back"
              data-testid="postif-approval-resend"
            >
              Resend sign link
            </Button>
          ) : (
            onBack && (
              <Button
                variant="outlined"
                size="small"
                onClick={onBack}
                className="pif-approval-review__back"
                data-testid="postif-approval-back"
              >
                Back to list
              </Button>
            )
          )}
        </Box>
      </Box>

      <Box className={gridClass}>
        {hasRail && (
          <PifApprovalChainRail
            chain={chainDisplay}
            audit={record.audit}
            viewerIsCurrentSigner={record.viewerIsCurrentSigner}
          />
        )}
        <PifApprovalRecordPanel record={record} />
      </Box>

      <Box className="pif-approval-review__footer">
        {record.viewerIsCurrentSigner ? (
          <PifApprovalDecisionPanel roleMeta={roleMeta} onSign={handleSign} onSendBack={handleSendBack} onDecline={handleDecline} />
        ) : (
          <Typography className="pif-approval-review__readonly-note">
            This form isn&apos;t currently awaiting your signature.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PifApprovalReview;
